<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Button from '../../Button.svelte';
  import Breadcrumbs from '../../Breadcrumbs.svelte';
  import BackButton from '../../BackButton.svelte';
  import LoadingSkeleton from '../../LoadingSkeleton.svelte';
  import ErrorState from '../../ErrorState.svelte';
  import { api } from '../../../lib/api/client';
  import PaymentsService, { type OutgoingPayment, type UpdateOutgoingPaymentData } from '../../../lib/api/admin/payments.service';
  import toast from 'svelte-french-toast';

  export let paymentUuid: string = '';

  let isLoading = true;
  let error = '';
  let payment: OutgoingPayment | null = null;
  let l2gFeePercentage = 3.5;
  let l2gFeeAmount = 0;
  let isUpdating = false;
  let showMarkAsPaidModal = false;
  let transactionId = '';
  let transactionDate = '';
  let isDownloading = false;

  // Track previous UUID to reload when it changes
  let previousUuid = '';
  $: if (paymentUuid && paymentUuid !== previousUuid) {
    previousUuid = paymentUuid;
    loadPayment();
  }

  // Breadcrumbs items
  $: breadcrumbsItems = [
    { label: 'Admin', href: '/admin/dashboard' },
    {
      label: payment?.type === 'paid' && !payment?.charity_confirmed_receipt
        ? 'Pending Confirmation'
        : 'Completed Payments',
      href:
        payment?.type === 'paid' && !payment?.charity_confirmed_receipt
          ? '/admin/pending-confirmation'
          : '/admin/payments/outgoing'
    },
    { label: payment ? `Payment - ${payment.charity.name}` : 'Payment Detail' }
  ];

  function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  }

  function formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate();
      const year = date.getFullYear();
      return `${month} ${day}, ${year}`;
    } catch {
      return 'Invalid Date';
    }
  }

  async function loadPayment() {
    if (!paymentUuid) {
      error = 'Payment UUID is required';
      isLoading = false;
      return;
    }

    try {
      isLoading = true;
      error = '';

      // Fetch payments in batches until we find the one we need
      let foundPayment: OutgoingPayment | null = null;
      let page = 1;
      const perPage = 100;
      let hasMore = true;

      while (hasMore && !foundPayment) {
        const response = await PaymentsService.getOutgoingPayments({ 
          page, 
          per_page: perPage 
        });
        
        if (response.success && response.data) {
          const payments = Array.isArray(response.data) ? response.data : (response.data.items || []);
          foundPayment = payments.find(p => p.uuid === paymentUuid) || null;
          
          const totalPages = response.paging?.last_page || 1;
          hasMore = page < totalPages && !foundPayment;
          page++;
        } else {
          hasMore = false;
          if (page === 1) {
            error = response.message || 'Failed to load payment';
            toast.error(error);
          }
        }
      }
      
      if (foundPayment) {
        payment = foundPayment;
        l2gFeePercentage = payment.l2g_fee_percentage;
        l2gFeeAmount = payment.l2g_fee_amount;
      } else {
        error = 'Payment not found';
        toast.error(error);
      }
    } catch (err: any) {
      error = err.message || 'An error occurred while loading payment';
      toast.error(error);
    } finally {
      isLoading = false;
    }
  }

  async function handleUpdateL2GFee() {
    if (!payment || !paymentUuid) return;

    try {
      isUpdating = true;

      const updateData: UpdateOutgoingPaymentData = {
        l2g_fee_percentage: l2gFeePercentage
      };

      const response = await PaymentsService.updateOutgoingPayment(paymentUuid, updateData);

      if (response.success) {
        toast.success('L2G fee updated successfully');
        await loadPayment();
      } else {
        toast.error(response.message || 'Failed to update L2G fee');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to update L2G fee');
    } finally {
      isUpdating = false;
    }
  }

  function handleMarkAsPaidClick() {
    if (payment?.type === 'paid') {
      toast('This payment is already marked as paid');
      return;
    }
    showMarkAsPaidModal = true;
    transactionDate = new Date().toISOString().split('T')[0];
  }

  function handleCloseModal() {
    showMarkAsPaidModal = false;
    transactionId = '';
    transactionDate = '';
  }

  async function handleConfirmMarkAsPaid() {
    if (!payment || !paymentUuid) return;

    try {
      isUpdating = true;

      const updateData: UpdateOutgoingPaymentData = {
        mark_as_paid: true,
        transaction_id: transactionId || undefined,
        transaction_date: transactionDate || undefined
      };

      const response = await PaymentsService.updateOutgoingPayment(paymentUuid, updateData);

      if (response.success) {
        toast.success('Payment marked as paid successfully');
        showMarkAsPaidModal = false;
        await loadPayment();
      } else {
        toast.error(response.message || 'Failed to mark payment as paid');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to mark payment as paid');
    } finally {
      isUpdating = false;
    }
  }

  function handleBack() {
    if (payment?.type === 'paid' && !payment?.charity_confirmed_receipt) {
      push('/admin/pending-confirmation');
      return;
    }
    push('/admin/payments/outgoing');
  }

  function handleEmployerPaymentClick() {
    if (payment?.employer_payment_uuid) {
      push(`/admin/payments/received/${payment.employer_payment_uuid}`);
    }
  }

  async function handleDownloadInvoice() {
    if (!payment) return;

    if (payment.type !== 'paid') {
      toast.error('Receipt is only available for paid payments');
      return;
    }

    try {
      isDownloading = true;
      const response = await api.get<Blob>(`/admin/payments/outgoing/${payment.uuid}/receipt`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeCharityName = payment.charity.name.replace(/[^a-zA-Z0-9-_]/g, '-');

      link.href = url;
      link.download = `receipt-${safeCharityName}-${payment.transaction_date || 'receipt'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Receipt downloaded successfully');
    } catch (err: any) {
      console.error('Download receipt error:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to download receipt');
    } finally {
      isDownloading = false;
    }
  }

  // Recalculate L2G fee amount when percentage changes
  $: if (payment && l2gFeePercentage !== undefined) {
    l2gFeeAmount = (payment.amount * l2gFeePercentage) / 100;
  }

  onMount(() => {
    if (paymentUuid) {
      previousUuid = paymentUuid;
      loadPayment();
    } else {
      error = 'Payment UUID is required';
      isLoading = false;
    }
  });
</script>

<div class="payment-detail-page">
  <Breadcrumbs items={breadcrumbsItems} />
  <BackButton text="Back to Pending Confirmation" onClick={handleBack} />

  {#if isLoading}
    <div style="min-height: 400px; padding: 2rem;">
      <LoadingSkeleton lines={8} height="2rem" />
    </div>
  {:else if error}
    <ErrorState
      message="Failed to load payment"
      description={error}
      onRetry={loadPayment}
    />
  {:else if payment}
    <!-- Payment Header Section -->
    <div class="payment-header">
      <div class="header-info">
        <h1 class="payment-title">Payment Detail</h1>
        <div class="payment-meta">
          <span class="meta-item">Charity: {payment.charity.name}</span>
          {#if payment.employer_name}
            <span class="meta-separator">•</span>
            <span class="meta-item">Employer: {payment.employer_name}</span>
          {/if}
        </div>
      </div>
    </div>

    <!-- Payment Details Card -->
    <div class="payment-details-card">
      <div class="card-header">
        <h2>Payment Information</h2>
      </div>

      <div class="card-content">
        <div class="details-grid">
          <div class="detail-item">
            <span class="detail-label">Charity</span>
            <span class="detail-value">{payment.charity.name}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Amount</span>
            <span class="detail-value">{formatCurrency(payment.amount)}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Status</span>
            <span class="detail-value">
              <span class="status-badge status-{payment.type === 'paid' ? 'paid' : payment.type === 'to_make' ? 'to-make' : 'pending'}">
                {payment.type === 'paid' ? (payment.status || 'Paid') : payment.type === 'to_make' ? 'To Make' : 'Pending'}
              </span>
            </span>
          </div>
          {#if payment.transaction_id}
            <div class="detail-item">
              <span class="detail-label">Transaction ID</span>
              <span class="detail-value">{payment.transaction_id}</span>
            </div>
          {/if}
          {#if payment.transaction_date}
            <div class="detail-item">
              <span class="detail-label">Transaction Date</span>
              <span class="detail-value">{formatDate(payment.transaction_date)}</span>
            </div>
          {/if}
          {#if payment.employer_payment_uuid}
            <div class="detail-item">
              <span class="detail-label">Payment ID</span>
              <span class="detail-value">{payment.employer_payment_uuid.substring(0, 8).toUpperCase()}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">View Full Payment</span>
              <span class="detail-value">
                <button class="link-button" on:click={handleEmployerPaymentClick}>
                  View All Payment Lines
                </button>
              </span>
            </div>
          {/if}
        </div>
      </div>
    </div>

    <!-- L2G Fee Management Card -->
    <div class="l2g-fee-card">
      <div class="card-header">
        <h2>Transaction Fee Management</h2>
      </div>

      <div class="card-content">
        <div class="fee-management">
          <div class="fee-info">
            <div class="info-row">
              <span class="info-label">Payment Amount:</span>
              <span class="info-value">{formatCurrency(payment.amount)}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Transaction Fee Percentage:</span>
              <div class="fee-input-group">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  bind:value={l2gFeePercentage}
                  class="fee-input"
                  disabled={isUpdating || payment.type === 'paid'}
                />
                <span class="fee-unit">%</span>
              </div>
            </div>
            <div class="info-row">
              <span class="info-label">Transaction Fee Amount:</span>
              <span class="info-value">{formatCurrency(l2gFeeAmount)}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Amount to Charity:</span>
              <span class="info-value">{formatCurrency(payment.amount - l2gFeeAmount)}</span>
            </div>
          </div>

          {#if payment.type !== 'paid'}
            <div class="fee-actions">
              <Button
                variant="primary"
                on:click={handleUpdateL2GFee}
                disabled={isUpdating || l2gFeePercentage === payment.l2g_fee_percentage}
              >
                {isUpdating ? 'Updating...' : 'Update Transaction Fee'}
              </Button>
            </div>
          {/if}
        </div>
      </div>
    </div>

    <!-- Actions Card -->
    {#if payment.type !== 'paid'}
      <div class="actions-card">
        <div class="card-header">
          <h2>Actions</h2>
        </div>

        <div class="card-content">
          <div class="actions-grid">
            <Button
              variant="primary"
              on:click={handleMarkAsPaidClick}
              disabled={isUpdating}
            >
              Mark as Paid
            </Button>
            <Button
              variant="secondary"
              on:click={handleDownloadInvoice}
              disabled={isDownloading}
            >
              {isDownloading ? 'Downloading...' : 'Download Invoice'}
            </Button>
          </div>
        </div>
      </div>
    {:else}
      <div class="actions-card">
        <div class="card-header">
          <h2>Actions</h2>
        </div>

        <div class="card-content">
          <div class="actions-grid">
            <Button
              variant="secondary"
              on:click={handleDownloadInvoice}
              disabled={isDownloading}
            >
              {isDownloading ? 'Downloading...' : 'Download Invoice'}
            </Button>
          </div>
        </div>
      </div>
    {/if}
  {/if}

  <!-- Mark as Paid Modal -->
  {#if showMarkAsPaidModal}
    <div class="modal-overlay" role="presentation" on:click|self={handleCloseModal}>
      <div class="modal-content" role="dialog" aria-modal="true" aria-labelledby="mark-paid-modal-title">
        <div class="modal-header">
          <h3 id="mark-paid-modal-title">Mark Payment as Paid</h3>
          <button class="modal-close" on:click={handleCloseModal}>×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="transaction-id">Transaction ID (Optional)</label>
            <input
              id="transaction-id"
              type="text"
              bind:value={transactionId}
              placeholder="Enter transaction ID"
              class="form-input"
            />
          </div>
          <div class="form-group">
            <label for="transaction-date">Transaction Date</label>
            <input
              id="transaction-date"
              type="date"
              bind:value={transactionDate}
              class="form-input"
            />
          </div>
        </div>
        <div class="modal-footer">
          <Button variant="secondary" on:click={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" on:click={handleConfirmMarkAsPaid} disabled={isUpdating || !transactionDate}>
            {isUpdating ? 'Processing...' : 'Mark as Paid'}
          </Button>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .payment-detail-page {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem 2rem;
    background-color: #f7fafc;
    min-height: 100vh;
  }

  .payment-header {
    background: white;
    border-radius: 12px;
    padding: 2rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .header-info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .payment-title {
    font-size: 2rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  .payment-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #6b7280;
    font-size: 0.875rem;
  }

  .meta-separator {
    color: #d1d5db;
  }

  .payment-details-card,
  .l2g-fee-card,
  .actions-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .card-header {
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .card-header h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .card-content {
    padding: 1.5rem 2rem;
  }

  .details-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
  }

  .detail-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .detail-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }

  .detail-value {
    font-size: 1rem;
    color: #111827;
  }

  .link-button {
    background: none;
    border: none;
    color: #3b82f6;
    cursor: pointer;
    text-decoration: underline;
    font-size: 1rem;
    padding: 0;
  }

  .link-button:hover {
    color: #2563eb;
  }

  .fee-management {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .fee-info {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f3f4f6;
  }

  .info-row:last-child {
    border-bottom: none;
  }

  .info-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }

  .info-value {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
  }

  .fee-input-group {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .fee-input {
    width: 100px;
    padding: 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 1rem;
  }

  .fee-input:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }

  .fee-unit {
    font-size: 1rem;
    color: #6b7280;
  }

  .fee-actions {
    display: flex;
    justify-content: flex-end;
  }

  .actions-grid {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .status-badge.status-paid {
    background-color: #d1fae5;
    color: #065f46;
  }

  .status-badge.status-pending {
    background-color: #fef3c7;
    color: #92400e;
  }

  .status-badge.status-to-make {
    background-color: #dbeafe;
    color: #1e40af;
  }

  /* Modal Styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
  }

  .modal-content {
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    max-width: 500px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
  }

  .modal-header {
    padding: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .modal-header h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .modal-close {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #6b7280;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
  }

  .modal-close:hover {
    background-color: #f3f4f6;
    color: #111827;
  }

  .modal-body {
    padding: 1.5rem;
  }

  .form-group {
    margin-bottom: 1.5rem;
  }

  .form-group:last-child {
    margin-bottom: 0;
  }

  .form-group label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
  }

  .form-input {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 1rem;
    box-sizing: border-box;
  }

  .form-input:focus {
    outline: none;
    border-color: #f0b429;
    box-shadow: 0 0 0 3px rgba(240, 180, 41, 0.1);
  }

  .modal-footer {
    padding: 1.5rem;
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
  }
</style>

